/* import __COLOCATED_TEMPLATE__ from './optimize-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type SummaryTab } from './conversation-explore-panel';
import { action } from '@ember/object';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type IntlService from 'embercom/services/intl';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { EntityType } from 'embercom/models/data/entity-types';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { objectIcons, objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';

interface Signature {
  Args: {
    attributeLabel?: string;
    header: string;
    conversationCount?: number;
    summaryText: string;
    suggestions?: ContentWrapper[];
    onShowConversationExplorer?: (
      selectedConversationId: number | null,
      filters: LogicalFilter,
      attributeValuesToExplore: string[],
      summaryType: SummaryTab,
      summaryText: string,
      summaryHeading: string,
      conversationIdsToLoad?: number[],
    ) => void;
    processedFilters?: LogicalFilter;
    attributeValue?: string;
    handleViewConversations?: () => void;
    hasSuggestions: boolean;
    conversationIdsToLoad?: number[];
    isCompleted?: (suggestion: ContentWrapper) => boolean;
  };
}

export default class OptimizeCard extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare contentSuggestionsService: ContentSuggestionsService;

  get editorMode(): EditorMode.REVIEW {
    return EditorMode.REVIEW;
  }

  get displayedSuggestions(): ContentWrapper[] {
    return this.args.suggestions?.slice(0, 4) || [];
  }

  handleViewConversations = () => {
    // Use the provided handleViewConversations if available, otherwise use default behavior
    if (this.args.handleViewConversations) {
      this.args.handleViewConversations();
      return;
    }

    if (!this.args.processedFilters) {
      return;
    }

    this.args.onShowConversationExplorer?.(
      null,
      this.args.processedFilters,
      [this.args.attributeValue || ''],
      'attribute_drivers',
      this.args.summaryText,
      this.args.header,
      this.args.conversationIdsToLoad || [],
    );
  };

  @action
  getSuggestionIcon(suggestion: ContentWrapper): InterfaceIconName {
    let contentType = suggestion.entityType;
    if (!contentType) {
      return 'article';
    }
    return objectIcons[contentType] || 'article';
  }

  @action
  getSuggestionLabel(suggestion: ContentWrapper): string {
    let reviewType = suggestion.contentReviewType;
    let entityType = suggestion.entityType;
    let objectKey = entityType === EntityType.ContentSnippet ? 'snippet' : 'article';
    let translationKey: string;

    switch (reviewType) {
      case ReviewType.CREATE_CONTENT:
        translationKey = 'ai-agent.suggestions.list-item.titles.add-content';
        break;
      case ReviewType.EDIT_CONTENT:
        translationKey = `ai-agent.suggestions.list-item.titles.edit-${objectKey}`;
        break;
      case ReviewType.DELETE_CONTENT:
        translationKey = `ai-agent.suggestions.list-item.titles.delete-${objectKey}`;
        break;
      case ReviewType.DUPLICATE_CONTENT:
        translationKey = 'ai-agent.suggestions.list-item.titles.duplicate-content';
        break;
      case ReviewType.CONTRADICTING_CONTENT:
        translationKey = 'ai-agent.suggestions.list-item.titles.contradicting-content';
        break;
      case ReviewType.UNDERPERFORMING_CONTENT:
        translationKey = `ai-agent.suggestions.list-item.titles.underperforming-${objectKey}`;
        break;
      default:
        translationKey = `ai-agent.suggestions.list-item.titles.fallback-${objectKey}`;
    }

    let result = this.intl.t(translationKey);
    return result;
  }

  @action
  getEntityId(suggestion: ContentWrapper): number {
    return suggestion.entityId!;
  }

  @action
  getSuggestionContentObjectName(suggestion: ContentWrapper): string {
    let entityType = suggestion.entityType;
    if (!entityType) {
      return 'article';
    }
    return objectNames[entityType] || 'article';
  }

  @action
  isCompleted(suggestion: ContentWrapper): boolean {
    if (this.args.isCompleted) {
      return this.args.isCompleted(suggestion);
    }
    // Fallback to checking the service directly (used in tests)
    return this.contentSuggestionsService.isSuggestionCompleted({
      entityId: suggestion.entityId!,
      entityType: suggestion.entityType,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::OptimizeCard': typeof OptimizeCard;
    'reporting/automation/ai-insights/optimize/optimize-card': typeof OptimizeCard;
  }
}
